


























































import { Component, Vue, Watch } from 'vue-property-decorator'
import { Branch } from '@/api/model/branch'
import { ReportAPI } from '@/api/client/report'
import moment from 'moment'

interface MetricDataRecordSubject {
    id: string,
    name: string,
    firstname: string,
    middlename: string,
    lastname: string,
}

interface MetricDataRecord {
    subject: MetricDataRecordSubject,
    value: number,
}

@Component
export default class LeaderboardBlock extends Vue {
    loading: boolean = false
    metricData: MetricDataRecord[] = []
    metric: string = 'success'
    view: string = 'users'
    period: string = 'this-year'

    mounted() {
        this.view = this.$currentUser.defaultDashboardView === 'user' ? 'users' : 'branches'
        this.loadMetricData()
    }

    get availableMetrics(): string[] {
        return ['success', 'days-until-success', 'hours-until-start', 'conversion']
    }

    get availableMetricsOptions() {
        return this.availableMetrics
            .map(availableMetric => ({
                label: this.$t(`dashboard.leaderboard.metric.${availableMetric}.title`),
                value: availableMetric,
            }))
    }

    changeMetric(metric: string): void {
        if (!this.availableMetrics.includes(metric)) {
            return
        }

        this.metric = metric
    }

    get availableViews(): string[] {
        return ['users', 'branches']
    }

    get availableViewsOptions() {
        return this.availableViews
            .map(availableView => ({
                label: this.$t(`dashboard.leaderboard.view.${availableView}`),
                value: availableView,
            }))
    }

    changeView(view: string): void {
        if (!this.availableViews.includes(view)) {
            return
        }

        this.view = view
    }

    get availablePeriods(): string[] {
        return ['this-month', 'previous-month', 'this-year']
    }

    get availablePeriodsOptions() {
        return this.availablePeriods
            .map(availablePeriod => ({
                label: this.$t(`dashboard.leaderboard.period.${availablePeriod}`),
                value: availablePeriod,
            }))
    }

    changePeriod(period: string): void {
        if (!this.availablePeriods.includes(period)) {
            return
        }

        this.period = period
    }

    @Watch('metric')
    @Watch('view')
    @Watch('period')
    async reloadMetricData() {
        let scrollTop = null

        let element = this.$el
        while (element.parentElement) {
            element = element.parentElement
            if (element.scrollTop > 0) {
                scrollTop = element.scrollTop
                break
            }
        }

        await this.loadMetricData()

        if (scrollTop) {
            element.scrollTop = scrollTop
        }
    }

    async loadMetricData(): Promise<void> {
        let fromDate
        let untilDate

        switch (this.period) {
            case 'this-month':
                fromDate = moment().startOf('month')
                untilDate = moment(fromDate).endOf('month')
                break

            case 'previous-month':
                fromDate = moment().subtract('1', 'month').startOf('month')
                untilDate = moment(fromDate).endOf('month')
                break

            case 'this-year':
                fromDate = moment().startOf('year')
                untilDate = moment().endOf('year')
                break
        }

        this.loading = true
        this.metricData = await ReportAPI.getLeaderboard(this.metric, this.view, fromDate, untilDate)
        this.loading = false
    }

    formatSubject(subject: MetricDataRecordSubject): string {
        switch (this.view) {
            case 'users':
                return [subject.firstname, subject.middlename, subject.lastname]
                    .filter(value => value !== null && value !== '')
                    .join(' ')

            case 'branches':
                return subject.name
        }

        return subject.id
    }

    formatValue(value: number): string {
        switch (this.metric) {
            case 'success':
                return Number(value).toLocaleString('nl', { minimumFractionDigits: 0, maximumFractionDigits: 0 })

            case 'conversion':
                return Number(value).toLocaleString('nl', { style: 'percent' })
        }

        return Number(value).toLocaleString('nl', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    }

    isCurrentUser(subject: MetricDataRecordSubject): boolean {
        switch (this.view) {
            case 'users':
                return this.$currentUser.id === subject.id

            case 'branches':
                return this.$currentUser.getAvailableBranches().some(branch => {
                    return branch.id === subject.id
                })
        }

        return false
    }
}
